import * as normalize from 'utils/normalize';
import sortInsurance from 'utils/sortInsurance';

import getPatientName from 'utils/print/getPatientName';
import getPatientFullDob from 'utils/print/getPatientFullDob';
import getBusinessPhone from 'utils/print/getBusinessPhone';
import getBusinessFax from 'utils/print/getBusinessFax';
import getBusinessStreet from 'utils/print/getBusinessStreet';
import getBusinessCityStateZip from 'utils/print/getBusinessCityStateZip';
import getBusinessEmail from 'utils/print/getBusinessEmail';
import getFacilityEmail from 'utils/print/getFacilityEmail';
import getFacilityName from 'utils/print/getFacilityName';
import getFacilityStreet from 'utils/print/getFacilityStreet';
import getFacilityCityStateZip from 'utils/print/getFacilityCityStateZip';
import getFacilityPhone from 'utils/print/getFacilityPhone';
import getFacilityFax from 'utils/print/getFacilityFax';
import getBusinessName from 'utils/print/getBusinessName';
import getCurrentUserName from 'utils/print/getCurrentUserName';
import getCurrentUserEmail from 'utils/print/getCurrentUserEmail';
import getOrderAuthorization from 'utils/print/getOrderAuthorization';
import getCurrentDate from 'utils/print/getCurrentDate';
import formatPrescriberName from 'utils/formatPrescriberName';

const HIPAA_DISCLAIMER = 'This fax, including any attachments may contain materials protected and '
  + 'governed by the Health Insurance and Portability and Accountability Act '
  + '(HIPAA). This fax and any files transmitted with it are confidential and are '
  + 'intended solely for the use of the individual/ entity to which they are '
  + 'addressed. The authorized recipient of this information is prohibited from '
  + 'disclosing this information to any other party unless authorized. If you are '
  + 'not the intended recipient of this fax please note that you have received it '
  + 'in error and any use, dissemination, forwarding, printing or copying of this '
  + 'fax is strictly prohibited. If you have received this fax in error, please '
  + 'immediately contact the sender of this message.';

const FaxCoverSheet = {
  ID: 52,
  Name: 'Fax Cover Sheet',
  Type: 'General',
  Format: 'letter',
  Orientation: 'p',
  BatchPrintPosition: 1,
  onChange: (values, dispatch, props, previousValues) => {
    if (values['Subject'] && values['Subject'] !== previousValues['Subject']) {
      switch (values['Subject']) {
        case 'Prior Authorization Request':
          dispatch(props.change('Body', values['Prior Authorization Request Body']));
          break;
        case 'Progress Note and/or Last Visit Notes':
          dispatch(props.change('Body', values['Progress Note and/or Last Visit Notes Body']));
          break;
        case 'No Auth Request':
          dispatch(props.change('Body', values['No Auth Request Body']));
          break;
        default:
          dispatch(props.change('Body', ''));
          break;
      }
    }
  },
  Pages: [
    {
      ID: 1,
      imageUrl: 'assets/documents/Fax-Cover-Sheet/1.js',
      Logo: {
        x: 8,
        y: 25,
        width: 50,
        height: 20,
      },
      texts: [
        {
          name: 'Business Name',
          getText: props => getFacilityName(props) || getBusinessName(props),
          x: 148,
          y: 26,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business Street',
          getText: props => getFacilityStreet(props) || getBusinessStreet(props),
          x: 148,
          y: 29,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business City State Zip',
          getText: props => getFacilityCityStateZip(props) || getBusinessCityStateZip(props),
          x: 148,
          y: 32,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business Phone',
          getText: props => getFacilityPhone(props) ? `Tel: ${getFacilityPhone(props)}` : `Tel: ${getBusinessPhone(props)}`,
          x: 148,
          y: 35,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business Fax',
          getText: props => getFacilityFax(props) ? `Fax: ${getFacilityFax(props)}` : `Fax: ${getBusinessFax(props)}`,
          x: 148,
          y: 38,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'Business Email',
          getText: props => getFacilityEmail(props) ? `Email: ${getFacilityEmail(props)}` : `Email: ${getBusinessEmail(props)}`,
          x: 148,
          y: 41,
          fontWeight: 'bold',
          fontSize: 7,
          lineHeight: 1.2,
        },
        {
          name: 'HIPAA Disclaimer',
          getText: () => HIPAA_DISCLAIMER,
          x: 100,
          y: 222,
          width: 104,
          fontSize: 8,
          lineHeight: 1.2,
        },
      ],
      fields: [
        {
          name: 'Attn',
          type: 'autosuggest',
          x: 24,
          y: 59,
          width: 120,
          height: 8,
          renderSuggestionContent: (suggestion) => suggestion.value,
          onSuggestionSelected: (suggestion, value, change) => {
            if (suggestion.phone) {
              change('Tel', suggestion.phone);
            } else {
              change('Tel', '');
            }
            if (suggestion.fax) {
              change('Fax', suggestion.fax);
            } else {
              change('Fax', '');
            }
            return suggestion.label;
          },
          fetchSuggestions: (props) => {
            const suggestions = props.order
              ? [
                ...props.order.Insurance.map((item) => {
                  const phoneContact = item['Insurance Info'].Contacts ? item['Insurance Info'].Contacts.find(contact => contact.type === 'Phone') : null;
                  const faxContact = item['Insurance Info'].Contacts ? item['Insurance Info'].Contacts.find(contact => contact.type === 'Fax') : null;
                  return {
                    value: `Payer: ${item['Insurance Info']['Payer Name']}`,
                    label: item['Insurance Info']['Payer Name'],
                    phone: phoneContact ? phoneContact.value : '',
                    fax: faxContact ? faxContact.value : '',
                  };
                }),
                ...props.order.Prescriber.ID ? [{
                  value: `Prescriber: ${formatPrescriberName(props.order.Prescriber)}`,
                  label: formatPrescriberName(props.order.Prescriber),
                  phone: props.order.Prescriber.Phone,
                  fax: props.order.Prescriber.Fax,
                }] : [],
                ...props.order['Referral Source']['Referral Source Contact']
                  ? props.order['Referral Source']['Referral Source Contact'].map(item => ({
                    value: item['Contact Name'] 
                      ? `Referral Source: ${props.order['Referral Source']['Name of source']} - ${item['Contact Name']}`
                      : `Referral Source: ${props.order['Referral Source']['Name of source']}`,
                    label: item['Contact Name'] 
                      ? `${props.order['Referral Source']['Name of source']} - ${item['Contact Name']}`
                      : `${props.order['Referral Source']['Name of source']}`,
                      phone: item.Phone,
                    fax: item.Fax,
                  })) : [],
              ] : [
                ...sortInsurance(props.patient['Insurance Information']).filter(item => item['Insurance Status'] === 'Active')
                  .map(item => ({
                    value: `Payer: ${item['Insurance Info']['Payer Name']}`,
                    label: item['Insurance Info']['Payer Name'],
                    fax: item['Insurance Info'].Fax,
                  })),
                ...props.patient.Prescriber.map(item => ({
                  value: `Prescriber: ${item['First Name']} ${item['Last Name']}`,
                  label: `${item['First Name']} ${item['Last Name']}`,
                  phone: item.Phone,
                  fax: item.Fax,
                })),
                ...props.patient['Referral Source'].reduce((accumulator, currentValue) => {
                  return [
                    ...accumulator,
                    ...currentValue['Referral Source Contact'].map(item => ({
                      value: item['Contact Name'] 
                        ? `Referral Source: ${currentValue['Name of source']} - ${item['Contact Name']}`
                        : `Referral Source: ${currentValue['Name of source']}`,
                      label: item['Contact Name'] 
                        ? `${currentValue['Name of source']} - ${item['Contact Name']}`
                        : `${currentValue['Name of source']}`,
                      phone: item.Phone,
                      fax: item.Fax,
                    })),
                  ];
                }, []),
              ];
            return new Promise((resolve) => {
              if (props.offset === 0) {
                if (props.value.length > 0) {
                  resolve(suggestions
                    .filter(item => item.label.toLowerCase().indexOf(props.value.toLowerCase()) !== -1));
                } else {
                  resolve(suggestions);
                }
              } else {
                resolve([]);
              }
            });
          },
        },
        {
          name: 'Tel',
          type: 'text',
          x: 20,
          y: 68.5,
          width: 80,
        },
        {
          name: 'Fax',
          type: 'text',
          x: 20,
          y: 73.5,
          width: 80,
        },
        {
          name: 'Pages',
          type: 'text',
          x: 24,
          y: 78.5,
          width: 10,
          normalize: normalize.positiveNumber,
        },
        {
          name: 'Request Date value',
          type: 'text',
          getValue: getCurrentDate,
          x: 179,
          y: 61,
          width: 20,
        },
        {
          name: 'Initial Request',
          type: 'checkbox',
          x: 150,
          y: 69.5,
          width: 4.5,
          height: 4.5,
        },
        {
          name: 'Follow Up',
          type: 'checkbox',
          x: 150,
          y: 78.5,
          width: 4.5,
          height: 4.5,
        },
        {
          name: 'Subject',
          type: 'autosuggest',
          x: 11,
          y: 106,
          width: 195,
          height: 8,
          fetchSuggestions: (props) => {
            const suggestions = [
              'Prior Authorization Request',
              'No Auth Request',
              'Progress Note and/or Last Visit Notes',
              'Physician Order Request',
              'Signature Request',
              'Medical Necessity Request',
              'Patient Summary Request',
            ];
            return new Promise((resolve) => {
              if (props.offset === 0) {
                if (props.value.length > 0) {
                  resolve(suggestions
                    .filter(item => item.toLowerCase().indexOf(props.value.toLowerCase()) !== -1));
                } else {
                  resolve(suggestions);
                }
              } else {
                resolve([]);
              }
            });
          },
        },
        {
          name: 'Patient Name',
          type: 'text',
          getValue: getPatientName,
          x: 42,
          y: 124,
          width: 80,
        },
        {
          name: 'Date of Birth',
          type: 'text',
          getValue: getPatientFullDob,
          x: 160,
          y: 124,
          width: 43,
          normalize: normalize.date,
        },
        {
          name: 'Body',
          type: 'text',
          x: 11,
          y: 138,
          width: 194,
          height: 70,
          multiline: true,
        },
        {
          name: 'Prior Authorization Request Body',
          type: 'text',
          getValue: props =>
            `PREVIOUS PRIOR AUTHORIZATION #: ${getOrderAuthorization(props) || ''}
          
Good Day,

We would like to request for the patient's doctor to fill out the MCD Prescriber worksheet for Enteral Formula Prior Authorization for:

PATIENT'S NAME: ${getPatientName(props)}
DATE OF BIRTH: ${getPatientFullDob(props)}
for his/her ENTERAL supplies.

Please be reminded that we cannot provide/continue any service without the requested
documents.`,
        },

        {
          name: 'Progress Note and/or Last Visit Notes Body',
          type: 'text',
          getValue: props =>
            `Good Day

May we request a RECENT/ UPDATED PROGRESS NOTES for:

PATIENT'S NAME: ${getPatientName(props)}
DATE OF BIRTH: ${getPatientFullDob(props)}
for his/ her monthly supplies.

For your reference, attached herein is the PRESCRIPTION we have received.`,
        },
        {
          name: 'No Auth Request Body',
          type: 'text',
          getValue: props =>
            `if no auth is required please send over no auth req letter/notes to our

FAX# ${getFacilityFax(props) || getBusinessFax(props)}
OR ${getFacilityEmail(props) || getBusinessEmail(props)}. 

Thank you.`,
        },
        {
          name: 'Current User Name',
          type: 'text',
          getValue: getCurrentUserName,
          x: 10,
          y: 226,
          width: 78,
        },
        {
          name: 'Current User Role',
          type: 'text',
          getValue: () => 'Documents Specialist',
          x: 10,
          y: 232,
          width: 78,
        },
        {
          name: 'User Business Phone',
          type: 'text',
          getValue: props => getFacilityPhone(props) || getBusinessPhone(props),
          x: 20,
          y: 239.5,
          width: 68,
        },
        {
          name: 'User BusinessFax',
          type: 'text',
          getValue: props => getFacilityFax(props) || getBusinessFax(props),
          x: 20,
          y: 245,
          width: 68,
        },
        {
          name: 'Current User Email',
          type: 'text',
          getValue: getCurrentUserEmail,
          x: 24,
          y: 250.5,
          width: 66,
        },
      ],
    },
  ],
};

export default FaxCoverSheet;



// WEBPACK FOOTER //
// ./src/config/documents/Fax-Cover-Sheet.js